import {MatTreeNestedDataSource} from '@angular/material/tree';
import {NestedTreeControl} from '@angular/cdk/tree';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnChanges, inject, WritableSignal, signal
} from '@angular/core';
import {FieldParameters} from '../../../core/definitions/field-parameters';
import {OptionsService} from '../../../core/options.service';
import {HierarchicNode} from '../../../core/definitions/hierarchic-objects';
import {CurrentQuery} from '../../../core/definitions/current-query';
import {FieldType} from '../../../core/definitions/field-type.enum';
import {OptionsDialogService} from '../../options-dialog.service';
import {FieldChoice, Reference} from "../../../core/definitions/reference";
import {
  HierarchicListPanelComponent
} from '../hierarchic-list-panel/hierarchic-list-panel.component';
import {MatButtonToggleChange} from "@angular/material/button-toggle";
import {loadUnloadedNonLeafChildren} from '../hierarchic-search-children';

@Component({
    selector: 'app-hierarchic-search-panel',
    templateUrl: './hierarchic-search-panel.component.html',
    styleUrls: ['./hierarchic-search-panel.component.scss'],
    standalone: false
})
export class HierarchicSearchPanelComponent implements OnChanges {
  // Injections
  private readonly optionsService: OptionsService = inject(OptionsService);
  private readonly optionsDialogService: OptionsDialogService = inject(OptionsDialogService);

  @ViewChild('listPanel') listPanel: HierarchicListPanelComponent;
  @ViewChild('queryInput') queryInput: ElementRef;
  @Input() fieldParameters: FieldParameters;
  @Input() reference: Reference;
  @Input() query: CurrentQuery;
  @Input() searchPanelOpen: boolean;
  @Output() nodeSelected = new EventEmitter<HierarchicNode>();
  @Output() nodeOpened = new EventEmitter<HierarchicNode>();

  rootNode: HierarchicNode;

  searching = false;
  private searchStartTime: number;
  private searchTimeoutId: number;
  private page = 0;
  private pageSize = 400;
  treeControl = new NestedTreeControl<HierarchicNode>(node => node.children);
  dataSource = new MatTreeNestedDataSource<HierarchicNode>();
  isArray: boolean;

  selectedSearchField: WritableSignal<string> = signal('');

  ngOnChanges(): void {
    if (this.reference.field_choices) {
      this.selectedSearchField.set(this.reference.field_choices[0].query_field);

      if (localStorage.getItem('primus.fieldSelectorChoices')) {
        let selectorChoices = JSON.parse(localStorage.getItem('primus.fieldSelectorChoices'));

        if (selectorChoices.hasOwnProperty(this.fieldParameters.field.reference_id)) {
          this.selectedSearchField.set(selectorChoices[this.fieldParameters.field.reference_id]);
        }
        else {
          this.selectedSearchField.set(this.reference.field_choices[0].query_field);
        }
      }
    }

    this.isArray = this.fieldParameters.field.field_type === FieldType.ARRAY ||
      this.fieldParameters.field.field_type === FieldType.MAP_ID_ARRAY;
    if (this.searchPanelOpen) {
      setTimeout(() => {
        this.queryInput.nativeElement.focus();
      }, 100);
    }
  }

  async queryChanged() {
    if (this.searchTimeoutId) {
      clearTimeout(this.searchTimeoutId);
      this.searchTimeoutId = 0;
    }
    this.searchTimeoutId = window.setTimeout(() => {
      this.getNodes().then();
    }, 300);
  }

  clearQuery() {
    this.query.value = '';
    this.resetNodes();
  }

  onNodeSelected(node: HierarchicNode, isLeafNode: boolean) {
    if (!isLeafNode && this.reference.hierarchic_parent_select_forbidden) {
      if (!this.treeControl.isExpanded(node)) {
        this.treeControl.expand(node);
        this.onNodeOpened(node);
      } else {
        this.treeControl.collapse(node);
      }
    } else if (!node.$$disabled) {
      this.nodeSelected.emit(node);
    }
  }

  async onNodeOpened(node: HierarchicNode) {
    this.nodeOpened.emit(node);
    const loaded = await loadUnloadedNonLeafChildren(
      node,
      this.fieldParameters,
      this.reference,
      this.pageSize,
      (parent, fieldParameters, reference, page, pageSize, parentId) =>
        this.optionsService.addHierarchicNodes(parent, fieldParameters, reference, page, pageSize, parentId));
    if (loaded) {
      this.sortLoadedChildren(node);
      this.refreshSearchTree(node);
    }
  }

  async onNodeLoadMore(node: HierarchicNode) {
    const page = Math.floor((node.$$rows || 0) / this.pageSize);
    await this.optionsService.addHierarchicNodes(
      node.$$parentNode,
      this.fieldParameters,
      this.reference,
      page,
      this.pageSize,
      node.parent_id);
    if (node.$$parentNode) {
      this.sortLoadedChildren(node.$$parentNode);
    }
    this.refreshSearchTree(node.$$parentNode);
  }

  private sortLoadedChildren(node: HierarchicNode) {
    const loadMore = (node.children || []).filter(child => child.$$nodeType === 'loadMoreNode');
    const children = (node.children || []).filter(child => child.$$nodeType !== 'loadMoreNode');
    node.children = this.sortNodes(children).concat(loadMore);
  }

  isLoadMore = (_: number, node: HierarchicNode) => node.$$nodeType === 'loadMoreNode';

  // is_leaf ignores the reference filter, so a non-leaf may have no children the user can pick.
  hasChild = (_: number, node: HierarchicNode) =>
    node.$$nodeType !== 'loadMoreNode'
    && (!!node.children?.length || (node.is_leaf === false && !node.$$childrenLoaded));

  toggleButtonChange(event: MatButtonToggleChange) {
    this.selectedSearchField.set(event.value);

    this.setUpdateLocalStorageToggleData(this.fieldParameters.field.reference_id, event.value);

    this.queryChanged().then();
  }

  // Will be called upon by parent component, must thus be public
  setNodeIsSelectedById(itemId, isSelected) {
    if (this.listPanel) {
      this.listPanel.setNodeIsSelectedById(itemId, isSelected);
    }
    this.optionsService.setNodeIsSelectedById(this.rootNode, itemId, isSelected);
  }

  private setSelectedHierarchicNodes() {
    this.optionsService.setSelectedHierarchicNodes(this.rootNode, this.fieldParameters);
  }

  async setListPanelNodes() {
    if (this.listPanel) {
      this.listPanel.setNodes().then();
    }
  }

  private getSelectedSearchField(): FieldChoice {
    for (let choice of this.reference.field_choices) {
      if (choice.query_field === this.selectedSearchField()) {
        return choice;
      }
    }

    return this.reference.field_choices[0];
  }

  private async getNodes() {
    if (this.query.value.length >= 1 && !this.searching) {
      this.searching = true;
      this.searchStartTime = (new Date()).getTime();
      // Here you can test reference field choice
      // const reference: Reference = this.optionsService.getReference(this.fieldParameters);
      if (this.reference.field_choices) {
         // Just for testing purpose, select 0 or 1 choice
         this.fieldParameters.selectedReferenceFieldChoice = this.getSelectedSearchField();
      }
      this.rootNode = await this.optionsService.hierarchicQuerySearch(
        this.fieldParameters, this.query.value, this.page, this.pageSize);
      this.setSelectedHierarchicNodes();
      // Keep Solr relevance order only when the search actually used score sort
      // (e.g. not when node display is code, which still uses legacy fq).
      const children = this.rootNode?.children || [];
      this.dataSource.data = this.rootNode?.$$relevanceSorted
        ? children
        : this.sortNodes(children);
      this.treeControl.dataNodes = this.dataSource.data;
      this.treeControl.expandAll();
      this.collapseUnloadedNonLeaves(this.dataSource.data);
      this.searching = false;
    } else if (!this.query.value.length) {
      this.resetNodes();
    }
    if (this.searching && (new Date()).getTime() - this.searchStartTime > 30000) {
      this.searching = false;
      console.warn('Restarting search due to timeout');
    }
  }

  private refreshSearchTree(keepExpanded?: HierarchicNode) {
    const expanded = this.treeControl.expansionModel?.selected
      ? [...this.treeControl.expansionModel.selected]
      : [];
    const nodes = this.dataSource.data || [];
    // A nested tree node reads its children only when it is rendered, so children loaded later
    // are drawn only after the nodes are rendered again.
    this.dataSource.data = [];
    this.dataSource.data = nodes;
    this.treeControl.dataNodes = nodes;
    for (const node of expanded) {
      this.treeControl.expand(node);
    }
    if (keepExpanded) {
      this.treeControl.expand(keepExpanded);
    }
  }

  private collapseUnloadedNonLeaves(nodes: HierarchicNode[]) {
    for (const node of nodes || []) {
      if (node.is_leaf === false && !node.$$childrenLoaded && !(node.children || []).some(child => child.$$nodeType !== 'loadMoreNode')) {
        this.treeControl.collapse(node);
      }
      this.collapseUnloadedNonLeaves(node.children);
    }
  }

  private sortNodes(nodes) {
    for (const node of nodes) {
      if (node.children?.length) {
        node.children = this.sortNodes(node.children);
      }
    }

    return nodes.sort(this.naturalSort);
  }

  private readonly naturalSort = (a: HierarchicNode, b: HierarchicNode): number => {
    return new Intl.Collator('nb-NO', {numeric: true, sensitivity: 'base'}).compare(a['$$name'], b['$$name']);
  }

  private resetNodes() {
    this.dataSource.data = [];
    this.treeControl.dataNodes = [];
  }

  private setUpdateLocalStorageToggleData(key: string, value: string): void {
    let currentDataString = localStorage.getItem('primus.fieldSelectorChoices');
    let currentDataJson;

    if (currentDataString) {
      currentDataJson = JSON.parse(currentDataString);
    }
    else {
      currentDataJson = {};
    }

    currentDataJson[key] = value;

    localStorage.setItem('primus.fieldSelectorChoices', JSON.stringify(currentDataJson));
  }

  async openDescription(node: HierarchicNode) {
    node.$$description = node['description.description'];
    const nodeDisplayName = await this.optionsService.getNodeDisplayField(this.reference, 'name.name');
    node.$$name = node[nodeDisplayName] || node['name.name'];
    this.optionsDialogService.toggleDescription(node);
  }

  getChildCount(node: any): number {
    // @ts-ignore
    return node.children?.length ?? 0;
  }

  getDepthClass(level: number): string {
    return `child-depth-${level + 1}`;
  }

  getNodeDisplayString(node: any) {
    return this.optionsService.getNodeDisplayString(
      this.fieldParameters, node, this.selectedSearchField());
  }

  useNewDesign(): boolean {
    return !!this.reference.use_new_design;
  }
}
